/**
 * OrgChart — organigram (struktura organizacyjna) tenanta, tryb PODGLĄDU.
 *
 * F24 Pakiet 0 rebase: kanwa (`OrgChartCanvas`) rysuje teraz DRZEWO JEDNOSTEK
 * organizacyjnych (`GET /org-units/tree`) z pracownikami pogrupowanymi pod swoją
 * jednostką — nie hierarchię pracownik→pracownik po `managerEmployeeId` (ta relacja
 * jest dziś wyjątkowym NADPISANIEM, domyślny przełożony wynika z kierownika jednostki,
 * wyliczany po stronie serwera; klient nie może już wiarygodnie zrekonstruować pełnej
 * hierarchii z samych rekordów kadrowych). Zakres firmy z przełącznika firm
 * (`useActiveCompanyScope`) — w trybie „Wszystkie firmy" wykres nie ma jednej firmy do
 * pokazania (jednostki są per-firma), więc ekran prosi o wybranie konkretnej firmy.
 *
 * Read-only, bez przeciągania/edycji (patrz brief F24 Pakiet 0 — „keep it simple").
 * Edycja struktury (dodawanie/przenoszenie/kierownik jednostki) żyje w Ustawieniach →
 * zakładka „Struktura" (`OrgStructureTab` → `CompanyDetailDrawer.orgUnits.tsx`).
 *
 * Odczyt drzewa wymaga PODGLĄDU firmy (`crm_core.companies.view`), nie zarządzania jednostkami
 * (fix 2026-10-05). Błąd odczytu nie jest już maskowany jako „brak struktury": 403 → stan
 * „brak uprawnień", inny błąd → stan z ponowieniem.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Network, ShieldOff } from 'lucide-react';

import { Button } from '@/components/Button';
import { OrgChartCanvas } from '@/components/org-chart/OrgChartCanvas';
import { EmptyState } from '@/components/ui/EmptyState';
import { formatDictionaryOptionLabel, useDictionary } from '@/hooks/useDictionary';
import { useActiveCompanyScope } from '@/lib/use-active-company';
import { useEmployeesList, useOrgUnitsTree } from '@/lib/use-tenant-people';

export function OrgChart() {
  const { t } = useTranslation();
  const scope = useActiveCompanyScope();
  const companyId = scope.companyId ?? null;

  const employeesQuery = useEmployeesList({
    companyId: companyId ?? undefined,
    isActive: true,
    limit: 200,
  });
  const treeQuery = useOrgUnitsTree(companyId);
  const positionsDict = useDictionary('employee_positions');

  const positionLabelByCode = useMemo(() => {
    const map = new Map<string, string>();
    if (!positionsDict.dictionary) return map;
    for (const item of positionsDict.items) {
      map.set(item.code, formatDictionaryOptionLabel(item, positionsDict.dictionary.displayMode));
    }
    return map;
  }, [positionsDict.dictionary, positionsDict.items]);

  const employees = employeesQuery.data?.employees ?? [];
  const tree = treeQuery.data?.tree ?? [];
  const isLoading = employeesQuery.isLoading || treeQuery.isLoading;
  const isEmpty = tree.length === 0 && employees.length === 0;
  const treeForbidden =
    treeQuery.isError && isAxiosError(treeQuery.error) && treeQuery.error.response?.status === 403;

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('tenant.orgChart.title')}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('tenant.orgChart.subtitle', { count: employees.length })}
        </p>
      </div>
      <div className="min-h-0 flex-1">
        {!companyId ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-gray-500 dark:text-gray-400">
            {t('tenant.orgChart.selectCompany')}
          </div>
        ) : isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-gray-400">
            {t('tenant.orgChart.loading')}
          </div>
        ) : treeForbidden ? (
          <div data-testid="org-chart-forbidden">
            <EmptyState
              icon={ShieldOff}
              title={t('tenant.orgChart.forbiddenTitle')}
              description={t('tenant.orgChart.forbiddenDescription')}
            />
          </div>
        ) : treeQuery.isError ? (
          <div data-testid="org-chart-error">
            <EmptyState
              icon={Network}
              title={t('tenant.orgChart.errorTitle')}
              description={t('tenant.orgChart.errorDescription')}
              action={
                <Button variant="secondary" size="sm" onClick={() => treeQuery.refetch()}>
                  {t('tenant.orgChart.retry')}
                </Button>
              }
            />
          </div>
        ) : isEmpty ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-gray-500 dark:text-gray-400">
            {t('tenant.orgChart.empty')}
          </div>
        ) : (
          <OrgChartCanvas
            tree={tree}
            employees={employees}
            positionLabelByCode={positionLabelByCode}
          />
        )}
      </div>
    </div>
  );
}
